import type { Bar, BarChart, ChartData, Line, PieChart } from '@trinity/chat';
import { barToPie, pieToBar } from './chart.utils';
import type { FilterRow } from './Chat.Chart.Filter.Context';

export type ChartPayloadData = PieChart['data'] | BarChart['data'] | Bar[] | Line | Line[];

export function isPieData(data: unknown[]): data is PieChart['data'] {
  return (
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'id' in data[0] &&
    'label' in data[0]
  );
}

export function isBarData(data: unknown[]): data is BarChart['data'] {
  return (
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'x' in data[0] &&
    'y' in data[0] &&
    !('id' in data[0])
  );
}

export function isMultipleBarData(data: unknown[]): data is Bar[] {
  return (
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'index' in data[0] &&
    'data' in data[0]
  );
}

export function isSingleLineData(data: ChartPayloadData): data is Line {
  return !Array.isArray(data) && data != null && typeof data === 'object' && 'id' in data && 'data' in data;
}

export function isMultipleLineData(data: unknown[]): data is Line[] {
  return (
    data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'id' in data[0] && 'data' in data[0]
  );
}

export function convertToChartDataPoints(
  data: ChartPayloadData
): { id?: string; label?: string; x?: string; value?: number; y?: number }[] {
  if (!data) return [];

  if (Array.isArray(data)) {
    if (isPieData(data)) return data;
    if (isBarData(data)) return data;
    if (isMultipleBarData(data)) {
      return data.flatMap((bar) => bar.data.map((point) => ({ x: point.x, y: point.y, label: bar.index })));
    }
    if (isMultipleLineData(data)) {
      return data.flatMap((line) =>
        line.data.map((point) => ({
          x: typeof point.x === 'string' ? point.x : point.x.toString(),
          y: point.y,
          label: line.id,
        }))
      );
    }
  }

  if (isSingleLineData(data)) {
    return data.data.map((point) => ({
      x: typeof point.x === 'string' ? point.x : point.x.toString(),
      y: point.y,
      label: data.id,
    }));
  }

  return [];
}

export function convertChartPayload(
  from: string | null,
  to: string,
  payload: ChartData['chart']['chart']
): ChartData['chart']['chart'] {
  if ((from === 'bar' || from === 'stacked-bar') && to === 'pie') {
    return barToPie(payload as BarChart);
  }
  if (from === 'pie' && (to === 'bar' || to === 'stacked-bar')) {
    return pieToBar(payload as PieChart);
  }
  return payload;
}

export function convertChartData(from: string, to: string, data: ChartPayloadData): ChartPayloadData {
  if ((from === 'bar' || from === 'stacked-bar') && to === 'pie') {
    const barData = data as { x: string; y: number }[];
    return barData.map((d) => ({ id: d.x, label: d.x, value: d.y }));
  }
  if (from === 'pie' && (to === 'bar' || to === 'stacked-bar')) {
    const pieData = data as { id: string; label: string; value: number }[];
    return pieData.map((d) => ({ x: d.label, y: d.value }));
  }
  return data;
}

export function getSelectedLabels(filters: FilterRow[]): string[] {
  return filters
    .filter((filterRow) => filterRow.selectedOption?.value === 'selected')
    .flatMap((filterRow) => (filterRow.valueOption ?? []).map((option) => option.value));
}

export function getNotSelectedLabels(filters: FilterRow[]): string[] {
  return filters
    .filter((filterRow) => filterRow.selectedOption?.value === 'not_selected')
    .flatMap((filterRow) => (filterRow.valueOption ?? []).map((option) => option.value));
}

export function applyLabelFilters<T>(
  data: T[],
  labelAccessor: (item: T) => string,
  filters: FilterRow[]
): T[] {
  const selectedLabels = getSelectedLabels(filters);
  const notSelectedLabels = getNotSelectedLabels(filters);
  let result = data;
  if (selectedLabels.length > 0) {
    result = result.filter((item) => selectedLabels.includes(labelAccessor(item)));
  }
  if (notSelectedLabels.length > 0) {
    result = result.filter((item) => !notSelectedLabels.includes(labelAccessor(item)));
  }
  return result;
}

export function applyChartFilters(
  chartType: string,
  originalData: ChartPayloadData,
  filters: FilterRow[]
): ChartPayloadData {
  if (chartType === 'pie') {
    const data = (originalData as PieChart['data']).filter(
      (dp) => typeof dp === 'object' && dp !== null && typeof dp.label === 'string'
    );
    return applyLabelFilters(data, (dp) => dp.label, filters);
  }
  if (chartType === 'bar' || chartType === 'stacked-bar') {
    const data = (originalData as BarChart['data']).filter(
      (dp) => typeof dp === 'object' && dp !== null && typeof dp.x === 'string'
    );
    return applyLabelFilters(data, (dp) => dp.x, filters);
  }
  return originalData;
}

export function computeFilteredChartData(
  chartTypeLocal: string,
  originalData: ChartPayloadData,
  filters: FilterRow[]
): { shouldReset: boolean; filteredData: ChartPayloadData | null } {
  const hasAnySelection = filters.some(
    (filterRow) => Array.isArray(filterRow.valueOption) && filterRow.valueOption.length > 0
  );
  if (!hasAnySelection) {
    return { shouldReset: true, filteredData: null };
  }
  const filteredData = applyChartFilters(chartTypeLocal, originalData, filters);
  const isSupported = ['pie', 'donut', 'bar', 'stacked-bar'].includes(chartTypeLocal);
  return { shouldReset: false, filteredData: isSupported ? filteredData : null };
}

export function applyFilteredDataToChart(
  chartTypeLocal: string,
  originalData: ChartPayloadData,
  filters: FilterRow[],
  setChart: (updater: (prev: ChartData) => ChartData) => void
): void {
  const { shouldReset, filteredData } = computeFilteredChartData(chartTypeLocal, originalData, filters);

  if (shouldReset) {
    setChart((prev) => ({
      ...prev,
      chart: {
        ...prev.chart,
        chart: Object.assign({}, prev.chart.chart, { data: originalData }) as ChartData['chart']['chart'],
      },
    }));
    return;
  }

  if (filteredData) {
    setChart((prev) => ({
      ...prev,
      chart: {
        ...prev.chart,
        chart: Object.assign({}, prev.chart.chart, { data: filteredData }) as ChartData['chart']['chart'],
      },
    }));
  }
}
